<template>
	<div class="db2" :class="{ 'is-edit': isEditMode, 'is-palette-dragging': paletteDragging }">
		<div class="db2-skel" v-if="showSkeleton">
			<aside class="db2-skel-menu">
				<div class="hc-skel"><div class="hc-skel-line mid"></div></div>
				<div class="hc-skel" v-for="n in 8" :key="'m'+n"><div class="hc-skel-line"></div></div>
			</aside>
			<div class="db2-skel-main">
				<div class="hc-skel db2-skel-head">
					<div class="hc-skel-line mid"></div>
					<div class="hc-skel-line short"></div>
				</div>
				<div class="db2-skel-cards">
					<div class="hc-skel db2-skel-card" v-for="n in 8" :key="'c'+n">
						<div class="hc-skel-line short"></div>
						<div class="hc-skel-line mid"></div>
						<div class="hc-skel-line"></div>
					</div>
				</div>
			</div>
		</div>
		<template v-else>
			<div class="db2-editbar" v-if="isEditMode">
				<div class="db2-editbar-title">
					<span>Edit Dashboard</span>
					<strong>{{ draft.name }}</strong>
				</div>
				<div class="db2-editbar-actions">
					<ViewportToggle v-model="previewMode" />
					<v-btn class="haq-btn haq-btn-secondary" @click="cancelEdit">Cancel</v-btn>
					<v-btn class="haq-btn haq-btn-danger" :disabled="saving || deleting" @click="deleteOpen = true">Delete</v-btn>
					<v-btn class="haq-btn haq-btn-secondary" :disabled="saving || deleting" @click="shareOpen = true">Share</v-btn>
					<v-btn class="haq-btn haq-btn-primary" :disabled="saving || deleting" @click="save">{{ saving ? 'Saving…' : 'Save' }}</v-btn>
				</div>
			</div>
			<div class="db2-body">
				<div class="db2-left">
					<CardPalette
						v-if="isEditMode && canEdit"
						:show-grid="showGrid"
						@palette-down="onPaletteDown"
						@add-section="addSection"
						@update:showGrid="showGrid = $event === true"
					/>
					<DashboardMenu
						v-else
						:dashboards="listedDashboards"
						:folders="folders"
						:active-id="draft.objectId"
						:loading-id="loadingDashboardId"
						:can-edit="canEdit"
						@select-dashboard="goDashboard"
						@create-folder="createFolder"
						@create-dashboard="$emit('create-new')"
						@rename-dashboard="renameDashboard"
						@update-dashboard="patchListedDashboard"
						@delete-dashboard="deleteListedDashboard"
						@rename-folder="renameFolder"
						@delete-folder="deleteFolder"
					/>
				</div>
				<div class="db2-main">
					<div class="db2-crumb" v-if="!isEditMode">
						<AppBreadcrumbs
							label="Dashboards"
							:label-to="listPath"
							:allow-all-sites="true"
							:site-id="breadcrumbSiteId"
						>
							<v-icon>mdi-chevron-right</v-icon>
							<span class="screen-label">{{ draft.name }}</span>
						</AppBreadcrumbs>
					</div>
					<header class="db2-header" v-if="!isEditMode">
						<div
							class="db2-header-icon"
							:class="{ 'has-photo': !!headerPhotoUrl }"
							:style="headerIconStyle"
						>
							<v-icon v-if="!headerPhotoUrl">{{ draft.icon || 'mdi-view-dashboard' }}</v-icon>
						</div>
						<div class="db2-header-text">
							<h1>{{ draft.name }}</h1>
							<p v-if="draft.description">{{ draft.description }}</p>
						</div>
						<div class="db2-header-filters">
							<HaqSelect
								v-model="timePeriod"
								label="Time Period"
								:items="timeRanges"
								item-text="name"
								item-value="value"
								hide-details
								:menu-props="{ bottom: true, offsetY: true, closeOnClick: true, closeOnContentClick: true }"
							/>
							<button
								type="button"
								class="db2-icon-btn"
								:class="{ 'is-refreshing': refreshing }"
								title="Refresh"
								:disabled="refreshing"
								@click="refresh"
							>
								<v-icon small>mdi-refresh</v-icon>
							</button>
							<v-btn
								v-if="canEdit"
								class="haq-btn haq-btn-primary"
								@click="enterEdit"
							>Edit</v-btn>
						</div>
					</header>
					<div
						class="db2-preview-frame"
						:class="{ 'is-framed': !!frameWidth }"
						:style="frameStyle"
					>
						<DashboardCanvas
							ref="canvas"
							:draft="draft"
							:is-edit-mode="isEditMode && activeCols === 12"
							:show-grid="showGrid === true && activeCols === 12"
							:selected-id="selectedId"
							:selected-section-id="selectedSectionId"
							:time-period="timePeriod"
							:site-id="draft.siteId || breadcrumbSiteId"
							:project-id="projectId"
							:refresh-key="refreshKey"
							:cols="activeCols"
							:can-edit="canEdit"
							@add-section="addSection"
							@rename-section="renameSection"
							@remove-section="askRemoveSection"
							@select-section="selectSection"
							@reorder-sections="reorderSections"
							@reorder-sections-end="onSectionReorderEnd"
							@toggle-section-collapsed="toggleSectionCollapsed"
							@select-card="selectCard"
							@duplicate-card="duplicateCard"
							@card-settings="openSettings"
							@remove-card="removeCard"
							@move-card="moveCard"
							@append-log="appendCardLog"
						/>
					</div>
				</div>
				<DashboardSettings
					v-if="isEditMode && canEdit"
					:draft="draft"
					:sites="sites"
					@patch="patchDraft"
				/>
			</div>
		</template>
		<v-dialog v-model="deleteOpen" content-class="haq-dialog" max-width="480px" persistent>
			<v-card class="haq-dialog-card">
				<v-card-title class="haq-dialog-card-header">
					<h5>Delete dashboard</h5>
					<v-spacer />
					<v-icon class="close-icon" @click="deleteOpen = false">mdi-close</v-icon>
				</v-card-title>
				<v-card-text>
					Delete “{{ draft.name }}”? This cannot be undone.
				</v-card-text>
				<v-card-actions>
					<v-spacer />
					<v-btn class="haq-btn haq-btn-secondary" :disabled="deleting" @click="deleteOpen = false">Cancel</v-btn>
					<v-btn class="haq-btn haq-btn-danger" :disabled="deleting" @click="confirmDeleteDashboard">{{ deleting ? 'Deleting…' : 'Delete' }}</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
		<v-dialog v-model="headerSiteWarnOpen" content-class="haq-dialog" max-width="480px">
			<v-card class="haq-dialog-card">
				<v-card-title class="haq-dialog-card-header">
					<h5>Default site required</h5>
					<v-spacer />
					<v-icon class="close-icon" @click="headerSiteWarnOpen = false">mdi-close</v-icon>
				</v-card-title>
				<v-card-text>
					Select a default site to use the site photo as the header image.
				</v-card-text>
				<v-card-actions>
					<v-spacer />
					<v-btn class="haq-btn haq-btn-primary" @click="headerSiteWarnOpen = false">OK</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
		<v-dialog v-model="sectionDeleteOpen" content-class="haq-dialog" max-width="480px" persistent>
			<v-card class="haq-dialog-card">
				<v-card-title class="haq-dialog-card-header">
					<h5>Delete section</h5>
					<v-spacer />
					<v-icon class="close-icon" @click="closeSectionDelete">mdi-close</v-icon>
				</v-card-title>
				<v-card-text>
					{{ sectionDeleteMessage }}
				</v-card-text>
				<v-card-actions>
					<v-spacer />
					<v-btn class="haq-btn haq-btn-secondary" @click="closeSectionDelete">Cancel</v-btn>
					<v-btn class="haq-btn haq-btn-danger" @click="confirmRemoveSection">Delete</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
		<CardSettingsDialog
			v-model="settingsOpen"
			:card="settingsCard"
			:sites="sites"
			@save="applyCardSettings"
		/>
		<DashboardShareDialog
			v-model="shareOpen"
			:dashboard="shareSource"
			:project-id="projectId"
			@saved="onShareSaved"
		/>
		<div
			v-if="paletteGhost"
			class="db2-palette-ghost"
			:style="paletteGhostStyle"
		>
			<v-icon>{{ paletteGhost.icon }}</v-icon>
			<span>{{ paletteGhost.label }}</span>
		</div>
	</div>
</template>

<script>
import { mapState } from 'vuex';
import AppBreadcrumbs from '@/components/AppBreadcrumbs.vue';
import HaqSelect from '@/components/form/HaqSelect.vue';
import DashboardMenu from './DashboardMenu.vue';
import CardPalette from './CardPalette.vue';
import DashboardCanvas from './DashboardCanvas.vue';
import DashboardSettings from './DashboardSettings.vue';
import CardSettingsDialog from './CardSettingsDialog.vue';
import DashboardShareDialog from './DashboardShareDialog.vue';
import ViewportToggle from './ViewportToggle.vue';
import { sitesUserCanAccess } from '../../../assets/js/siteAccess';
import {
	TIME_RANGES,
	VIEWPORTS,
	CARD_TYPES,
	colsForWidth,
	cloneDraft,
	hydrateDashboardDraft,
	savePayload,
	newSection,
	newCard,
	makeId,
	newTextLogEntry,
	appendTextLog,
	snapLayout,
	resolveOverlaps,
	findDuplicateLayout,
	findBottomSlot,
	columnWidthPx,
	isV2Dashboard,
	dashboardSiteQuery,
	dashboardHeaderArt,
	dashboardSiteId,
	dashboardHeaderPhotoUrl,
	GRID_ROW,
	GRID_GAP
} from './dashboardModel';
import './dashboard-v2.sass';

/**
 * Dashboard detail shell: menu, canvas, edit chrome, save-immediate.
 */
export default {
	name: 'DashboardWorkspace',
	components: {
		AppBreadcrumbs,
		HaqSelect,
		DashboardMenu,
		CardPalette,
		DashboardCanvas,
		DashboardSettings,
		CardSettingsDialog,
		DashboardShareDialog,
		ViewportToggle
	},
	props: {
		dashboard: { type: Object, required: true },
		initialEditMode: { type: Boolean, default: false }
	},
	data() {
		return {
			draft: hydrateDashboardDraft(this.dashboard),
			lastSaved: this.dashboard,
			isEditMode: false,
			showGrid: true,
			selectedId: null,
			selectedSectionId: null,
			timePeriod: 'week',
			timeRanges: TIME_RANGES,
			undoStack: [],
			redoStack: [],
			saving: false,
			deleteOpen: false,
			headerSiteWarnOpen: false,
			deleting: false,
			sectionDeleteOpen: false,
			sectionDeleteId: null,
			ready: false,
			refreshKey: 0,
			refreshing: false,
			settingsOpen: false,
			settingsCard: null,
			shareOpen: false,
			sites: [],
			previewMode: 'auto',
			windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1280,
			paletteGhost: null,
			paletteDragging: false,
			pendingDashboardId: null
		};
	},
	computed: {
		...mapState({
			dashboards: state => state.dashboards,
			folders: state => state.dashboardFolders,
			user: state => state.user
		}),
		/**
		 * Route project id.
		 * @returns {string|null}
		 */
		projectId() {
			return this.$route.params.projectId || (this.$store.state.project && this.$store.state.project.objectId) || null;
		},
		/**
		 * Whether the user may edit.
		 * @returns {boolean}
		 */
		canEdit() {
			const role = this.user && this.user.role;
			return role === 'admin' || role === 'superAdmin';
		},
		/**
		 * Dashboard row used by the share dialog (list copy, then last saved).
		 * @returns {object}
		 */
		shareSource() {
			const id = this.draft && this.draft.objectId;
			const listed = (this.dashboards || []).find(d => d && d.objectId === id);
			return listed || this.lastSaved || this.dashboard;
		},
		/**
		 * v2 dashboards plus the open dashboard (so a leftover v1 stays visible until deleted).
		 * @returns {Array}
		 */
		listedDashboards() {
			return (this.dashboards || []).filter(d => isV2Dashboard(d) || d.objectId === this.draft.objectId);
		},
		/**
		 * Skeleton until draft is mounted.
		 * @returns {boolean}
		 */
		showSkeleton() {
			return !this.ready;
		},
		/**
		 * Dashboard id being fetched after a side-panel click (route is ahead of draft).
		 * @returns {string|null}
		 */
		loadingDashboardId() {
			const id = this.pendingDashboardId || this.$route.params.dashboardId || null;
			if (!id || id === this.draft.objectId) return null;
			return id;
		},
		/**
		 * Undo available.
		 * @returns {boolean}
		 */
		canUndo() {
			return this.undoStack.length > 0;
		},
		/**
		 * Redo available.
		 * @returns {boolean}
		 */
		canRedo() {
			return this.redoStack.length > 0;
		},
		/**
		 * List route.
		 * @returns {object}
		 */
		listPath() {
			return { name: 'Dashboard', params: { projectId: this.projectId }, query: this.$route.query };
		},
		/**
		 * Breadcrumb site from query.
		 * @returns {string|null}
		 */
		breadcrumbSiteId() {
			return this.$route.query.siteId || null;
		},
		/**
		 * Site photo URL when header art is site photo.
		 * @returns {string}
		 */
		headerPhotoUrl() {
			return dashboardHeaderPhotoUrl(this.draft, this.sites);
		},
		/**
		 * Cover style for the header site photo.
		 * @returns {object}
		 */
		headerIconStyle() {
			const url = this.headerPhotoUrl;
			if (!url) return {};
			return { backgroundImage: 'url(' + url + ')' };
		},
		/**
		 * Column count for the current preview or live width.
		 * @returns {number}
		 */
		activeCols() {
			if (!this.isEditMode) return colsForWidth(this.windowWidth);
			const vp = VIEWPORTS.find(v => v.id === this.previewMode);
			if (vp && vp.cols) return vp.cols;
			return colsForWidth(this.windowWidth);
		},
		/**
		 * Fixed preview frame width, or null for live.
		 * @returns {string|null}
		 */
		frameWidth() {
			if (!this.isEditMode) return null;
			const vp = VIEWPORTS.find(v => v.id === this.previewMode);
			if (!vp || !vp.width) return null;
			return vp.width + 'px';
		},
		/**
		 * Inline style for the preview frame.
		 * @returns {object}
		 */
		frameStyle() {
			if (!this.frameWidth) return {};
			return { maxWidth: this.frameWidth, width: '100%' };
		},
		/**
		 * Fixed overlay box for a palette drag ghost.
		 * @returns {object}
		 */
		paletteGhostStyle() {
			const g = this.paletteGhost;
			if (!g) return {};
			return {
				left: g.left + 'px',
				top: g.top + 'px',
				width: g.width + 'px',
				height: g.height + 'px'
			};
		},
		/**
		 * Confirm copy for deleting a section and its cards.
		 * @returns {string}
		 */
		sectionDeleteMessage() {
			const section = (this.draft.sections || []).find(s => s.uniqueId === this.sectionDeleteId);
			const name = section && String(section.name || '').trim();
			const count = (this.draft.cards || []).filter(c => c.sectionId === this.sectionDeleteId).length;
			const cards = count === 1 ? '1 card' : count + ' cards';
			if (name) {
				return 'Delete section “' + name + '”? This will also delete all cards in this section (' + cards + '). This cannot be undone.';
			}
			return 'Delete this section? This will also delete all cards in this section (' + cards + '). This cannot be undone.';
		}
	},
	watch: {
		dashboard: {
			deep: true,
			handler(val) {
				if (!val) return;
				if (this.pendingDashboardId && val.objectId === this.pendingDashboardId) {
					this.pendingDashboardId = null;
				}
				const same = val.objectId === this.draft.objectId;
				if (this.isEditMode && same) return;
				this.lastSaved = val;
				this.draft = hydrateDashboardDraft(val);
				this.timePeriod = this.draft.defaultTimeRange || 'week';
				this.undoStack = [];
				this.redoStack = [];
				if (this.initialEditMode && this.canEdit) {
					this.isEditMode = true;
				}
			}
		},
		/**
		 * Drop Desktop/Tablet/Mobile preview when leaving edit.
		 * @param {boolean} on
		 */
		isEditMode(on) {
			if (!on) {
				this.previewMode = 'auto';
				this.clearNewQuery();
			}
		}
	},
	async mounted() {
		this.timePeriod = this.draft.defaultTimeRange || 'week';
		if (this.initialEditMode && this.canEdit) {
			this.isEditMode = true;
		}
		window.addEventListener('resize', this.onWinResize);
		await this.bootstrap();
		this.ready = true;
	},
	beforeDestroy() {
		this.clearPaletteGesture();
		window.removeEventListener('resize', this.onWinResize);
	},
	methods: {
		/**
		 * Track live viewport width.
		 */
		onWinResize() {
			this.windowWidth = window.innerWidth;
		},
		/**
		 * Load lists and accessible sites.
		 */
		async bootstrap() {
			if (this.projectId) {
				await Promise.all([
					this.$store.dispatch('getDashboards', this.projectId),
					this.$store.dispatch('getDashboardFolders', this.projectId).catch(() => {})
				]);
				if (this.canEdit) {
					await this.$store.dispatch('purgeLegacyDashboards', {
						projectId: this.projectId,
						keepId: this.draft.objectId
					});
				}
			}
			this.sites = sitesUserCanAccess(this.$store.state.sites || [], this.user, this.projectId);
			if (!this.sites.length) {
				try {
					const { HTTPClient } = await import('../../../assets/js/http');
					const client = new HTTPClient();
					const res = await client.robustQuery('/api', { slim: true });
					this.sites = sitesUserCanAccess(res.sites || [], this.user, this.projectId);
					this.$store.commit('setSites', this.sites);
				} catch (e) {
					console.error(e);
				}
			}
		},
		/**
		 * Spin the header control and reload canvas data.
		 */
		async refresh() {
			if (this.refreshing) return;
			this.refreshing = true;
			this.refreshKey += 1;
			const minSpin = new Promise(resolve => setTimeout(resolve, 700));
			try {
				await minSpin;
			} finally {
				this.refreshing = false;
			}
		},
		/**
		 * Snapshot current draft onto the undo stack.
		 */
		pushUndo() {
			this.undoStack.push(cloneDraft(this.draft));
			if (this.undoStack.length > 50) this.undoStack.shift();
			this.redoStack = [];
		},
		/**
		 * Patch draft fields.
		 * @param {object} patch
		 */
		patchDraft(patch) {
			this.pushUndo();
			this.draft = { ...this.draft, ...patch };
		},
		/**
		 * Undo last edit.
		 */
		undo() {
			if (!this.canUndo) return;
			this.redoStack.push(cloneDraft(this.draft));
			this.draft = this.undoStack.pop();
		},
		/**
		 * Redo.
		 */
		redo() {
			if (!this.canRedo) return;
			this.undoStack.push(cloneDraft(this.draft));
			this.draft = this.redoStack.pop();
		},
		/**
		 * Enter edit mode. Custom dashboards stay empty until the user adds a section.
		 */
		enterEdit() {
			this.isEditMode = true;
		},
		/**
		 * Leave edit and return to the list.
		 */
		backToList() {
			if (this.undoStack.length && !window.confirm('Discard unsaved changes?')) return;
			this.$emit('back');
		},
		/**
		 * Discard edits and stay in view mode.
		 */
		cancelEdit() {
			if (this.undoStack.length && !window.confirm('Discard unsaved changes?')) return;
			this.draft = hydrateDashboardDraft(this.lastSaved || this.dashboard);
			this.undoStack = [];
			this.redoStack = [];
			this.previewMode = 'auto';
			this.isEditMode = false;
			this.clearNewQuery();
		},
		/**
		 * Remove ?new=1 so a refresh does not re-enter edit.
		 */
		clearNewQuery() {
			if (this.$route.query.new == null) return;
			const query = Object.assign({}, this.$route.query);
			delete query.new;
			this.$router.replace({ query: query }).catch(() => {});
			this.$emit('cleared-new');
		},
		/**
		 * Full dashboard record for view mode after a successful save.
		 * @param {object} [apiRow]
		 * @returns {object}
		 */
		savedRecord(apiRow) {
			const fromDraft = {
				objectId: this.draft.objectId,
				site: this.draft.site || (this.dashboard && this.dashboard.site) || null,
				siteId: this.draft.siteId,
				...savePayload(this.draft)
			};
			const row = apiRow || {};
			return {
				...(this.dashboard || {}),
				...fromDraft,
				...row,
				cards: Array.isArray(row.cards) ? row.cards : fromDraft.cards,
				sections: Array.isArray(row.sections) ? row.sections : fromDraft.sections
			};
		},
		/**
		 * Keep lastSaved share fields after the share dialog saves.
		 * @param {object} row
		 */
		onShareSaved(row) {
			if (!row) return;
			this.lastSaved = Object.assign({}, this.lastSaved || {}, {
				shareProject: row.shareProject,
				sharedUsers: row.sharedUsers,
				sharedGroups: row.sharedGroups,
				sharedSites: row.sharedSites
			});
		},
		/**
		 * True when header image is site photo but no default site is set.
		 * @returns {boolean}
		 */
		headerPhotoMissingSite() {
			if (dashboardHeaderArt(this.draft) !== 'sitePhoto') return false;
			return !(dashboardSiteId(this.draft) || (this.draft && this.draft.siteId));
		},
		/**
		 * Persist immediately.
		 * @param {{ silent?: boolean }} [opts] MouseEvent from @click is ignored.
		 */
		async save(opts) {
			const silent = !!(opts && opts.silent);
			if (this.headerPhotoMissingSite()) {
				if (!silent) this.headerSiteWarnOpen = true;
				return false;
			}
			this.saving = true;
			try {
				this.draft.schemaVersion = 2;
				const result = await this.$store.dispatch('updateDashboard', {
					dashboardId: this.draft.objectId,
					updateData: savePayload(this.draft)
				});
				const snapshot = cloneDraft(this.savedRecord(result && result.results));
				this.lastSaved = snapshot;
				this.draft = hydrateDashboardDraft(snapshot);
				this.$emit('saved', cloneDraft(snapshot));
				if (!silent) {
					this.undoStack = [];
					this.redoStack = [];
					this.clearNewQuery();
					window.M && window.M.toast && window.M.toast({ html: 'Dashboard saved' });
				}
				await this.$store.dispatch('getDashboards', this.projectId);
				return true;
			} catch (e) {
				console.error(e);
				window.M && window.M.toast && window.M.toast({ html: 'Failed to save dashboard' });
				return false;
			} finally {
				this.saving = false;
			}
		},
		/**
		 * Append a timestamped note on a log card. View mode saves at once; edit mode waits for Save.
		 * @param {{ uniqueId: string, text: string }} payload
		 */
		async appendCardLog(payload) {
			if (!this.canEdit) return;
			const uniqueId = payload && payload.uniqueId;
			const text = String((payload && payload.text) || '').trim();
			if (!uniqueId || !text) return;
			const cards = (this.draft.cards || []).slice();
			const idx = cards.findIndex(c => String(c.uniqueId) === String(uniqueId));
			if (idx < 0) return;
			const entry = newTextLogEntry(text, this.user);
			if (!entry.text) return;
			if (this.isEditMode) this.pushUndo();
			const prev = cloneDraft(this.draft);
			const card = cards[idx];
			this.$set(cards, idx, { ...card, config: appendTextLog(card.config, entry) });
			this.draft = { ...this.draft, cards };
			if (this.isEditMode) return;
			const ok = await this.save({ silent: true });
			if (!ok) this.draft = prev;
		},
		/**
		 * Navigate to another dashboard.
		 * @param {string} id
		 */
		goDashboard(id) {
			if (!id || id === this.draft.objectId || id === this.pendingDashboardId) return;
			this.pendingDashboardId = id;
			const dashboard = (this.dashboards || []).find(d => d.objectId === id);
			this.$router.push({
				name: 'DashboardDetail',
				params: { projectId: this.projectId, dashboardId: id },
				query: dashboardSiteQuery(dashboard, this.$route.query)
			}).catch(() => {
				if (this.pendingDashboardId === id) this.pendingDashboardId = null;
			});
		},
		/**
		 * Selected section or last section id.
		 * @returns {string|null}
		 */
		targetSectionId() {
			if (this.selectedSectionId && (this.draft.sections || []).some(s => s.uniqueId === this.selectedSectionId)) {
				return this.selectedSectionId;
			}
			const sections = this.draft.sections || [];
			if (this.selectedId) {
				const card = (this.draft.cards || []).find(c => c.uniqueId === this.selectedId);
				if (card) return card.sectionId;
			}
			return sections.length ? sections[sections.length - 1].uniqueId : null;
		},
		/**
		 * Add a card from the palette click (bottom of the section) or a drop layout.
		 * @param {string} type
		 * @param {{ layout?: object, sectionId?: string, scroll?: boolean }} [opts]
		 * @returns {object|null}
		 */
		addCard(type, opts) {
			const options = opts || {};
			const meta = CARD_TYPES.find(t => t.type === type);
			if (meta && meta.disabled) return null;
			let sectionId = options.sectionId || this.targetSectionId();
			if (!sectionId) {
				this.addSection();
				sectionId = this.targetSectionId();
			}
			this.pushUndo();
			this.draft.schemaVersion = 2;
			const card = newCard(type, sectionId, this.draft.cards);
			if (options.layout) card.layout = snapLayout(options.layout, type);
			else card.layout = findBottomSlot(this.draft.cards, sectionId, card.layout.w, card.layout.h);
			this.draft.cards = (this.draft.cards || []).concat([card]);
			this.selectedId = card.uniqueId;
			this.selectedSectionId = sectionId;
			if (options.scroll !== false) this.scrollToCard(card.uniqueId);
			return card;
		},
		/**
		 * Scroll the preview so a card is visible.
		 * @param {string} uniqueId
		 */
		scrollToCard(uniqueId) {
			this.$nextTick(() => {
				const node = this.$el && this.$el.querySelector('[data-card-id="' + uniqueId + '"]');
				if (node && node.scrollIntoView) node.scrollIntoView({ behavior: 'smooth', block: 'end' });
			});
		},
		/**
		 * Grid element under the pointer, if any.
		 * @param {MouseEvent} e
		 * @returns {HTMLElement|null}
		 */
		gridAtClient(e) {
			const root = this.$el;
			if (!root) return null;
			const grids = root.querySelectorAll('.db2-grid');
			for (let i = 0; i < grids.length; i++) {
				const rect = grids[i].getBoundingClientRect();
				if (e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom) {
					return grids[i];
				}
			}
			return null;
		},
		/**
		 * Snapped layout for a palette drop at the pointer.
		 * @param {MouseEvent} e
		 * @param {number} w
		 * @param {number} h
		 * @param {string} [type]
		 * @returns {object}
		 */
		layoutAtClient(e, w, h, type) {
			const grid = this.gridAtClient(e);
			if (!grid) return snapLayout({ x: 0, y: 0, w, h }, type);
			const rect = grid.getBoundingClientRect();
			const cw = columnWidthPx(grid.clientWidth, 12) + GRID_GAP;
			const ch = GRID_ROW + GRID_GAP;
			const x = Math.floor((e.clientX - rect.left) / cw);
			const y = Math.floor((e.clientY - rect.top) / ch);
			return snapLayout({ x, y, w, h }, type);
		},
		/**
		 * Pixel size of a palette ghost for a card type.
		 * @param {{ w: number, h: number }} meta
		 * @returns {{ width: number, height: number }}
		 */
		ghostSize(meta) {
			const grid = this.$el && this.$el.querySelector('.db2-grid');
			const gw = grid ? grid.clientWidth : 960;
			const cw = columnWidthPx(gw, 12);
			const w = meta.w || 3;
			const h = meta.h || 2;
			return {
				width: w * cw + Math.max(0, w - 1) * GRID_GAP,
				height: h * GRID_ROW + Math.max(0, h - 1) * GRID_GAP
			};
		},
		/**
		 * Move or create the floating palette ghost.
		 * @param {MouseEvent} e
		 * @param {{ icon?: string, label?: string, w?: number, h?: number }} meta
		 */
		syncPaletteGhost(e, meta) {
			const size = this.ghostSize(meta);
			this.paletteGhost = {
				left: e.clientX + 12,
				top: e.clientY + 12,
				width: size.width,
				height: size.height,
				icon: meta.icon || 'mdi-card-outline',
				label: meta.label || 'Card'
			};
		},
		/**
		 * Insert the dragged palette type onto the grid and start a normal card move.
		 * @param {MouseEvent} e
		 */
		insertPaletteCard(e) {
			const g = this._palette;
			if (!g || g.cardId) return;
			const grid = this.gridAtClient(e);
			const sectionId = (grid && grid.getAttribute('data-section-id')) || this.targetSectionId();
			const card = this.addCard(g.type, {
				sectionId,
				layout: this.layoutAtClient(e, g.meta.w, g.meta.h, g.meta.type),
				scroll: false
			});
			if (!card) return;
			g.cardId = card.uniqueId;
			this.paletteGhost = null;
			this.$nextTick(() => {
				if (this.$refs.canvas && this.$refs.canvas.beginCardDrag) {
					this.$refs.canvas.beginCardDrag(card, e);
				}
				this.moveCard({ uniqueId: card.uniqueId, layout: card.layout, live: true });
			});
		},
		/**
		 * Palette mousedown: wait to see if this is a click or a drag.
		 * @param {{ type: string, event: MouseEvent }} payload
		 */
		onPaletteDown(payload) {
			const meta = CARD_TYPES.find(t => t.type === payload.type);
			if (!meta || meta.disabled) return;
			this.clearPaletteGesture();
			this._palette = {
				type: payload.type,
				meta,
				sx: payload.event.clientX,
				sy: payload.event.clientY,
				dragging: false,
				cardId: null
			};
			window.addEventListener('mousemove', this.onPaletteMove);
			window.addEventListener('mouseup', this.onPaletteUp);
		},
		/**
		 * Palette pointer move: show a ghost, then drop onto the grid and live-pack.
		 * @param {MouseEvent} e
		 */
		onPaletteMove(e) {
			const g = this._palette;
			if (!g) return;
			const dist = Math.hypot(e.clientX - g.sx, e.clientY - g.sy);
			if (!g.dragging && dist < 8) return;
			if (!g.dragging) {
				g.dragging = true;
				this.paletteDragging = true;
			}
			if (!g.cardId) this.syncPaletteGhost(e, g.meta);
			if (!g.cardId && this.gridAtClient(e)) this.insertPaletteCard(e);
		},
		/**
		 * Palette pointer up: click adds at the bottom; drag settles via the section.
		 * @param {MouseEvent} e
		 */
		onPaletteUp(e) {
			const g = this._palette;
			const wasDragging = !!(g && g.dragging);
			const alreadyIn = !!(g && g.cardId);
			this.clearPaletteListeners();
			this._palette = null;
			this.paletteGhost = null;
			this.paletteDragging = false;
			if (!g) return;
			if (!wasDragging) {
				this.addCard(g.type);
				return;
			}
			if (alreadyIn) return;
			const grid = this.gridAtClient(e);
			if (!grid) return;
			const card = this.addCard(g.type, {
				sectionId: grid.getAttribute('data-section-id') || this.targetSectionId(),
				layout: this.layoutAtClient(e, g.meta.w, g.meta.h, g.meta.type),
				scroll: false
			});
			if (card) this.moveCard({ uniqueId: card.uniqueId, layout: card.layout, settle: true });
		},
		/**
		 * Remove palette window listeners.
		 */
		clearPaletteListeners() {
			window.removeEventListener('mousemove', this.onPaletteMove);
			window.removeEventListener('mouseup', this.onPaletteUp);
		},
		/**
		 * Abort an in-flight palette gesture.
		 */
		clearPaletteGesture() {
			this.clearPaletteListeners();
			this._palette = null;
			this.paletteGhost = null;
			this.paletteDragging = false;
		},
		/**
		 * Add a named section.
		 */
		addSection() {
			this.pushUndo();
			this.draft.schemaVersion = 2;
			const section = newSection('Section', (this.draft.sections || []).length);
			this.draft.sections = (this.draft.sections || []).concat([section]);
			this.selectedSectionId = section.uniqueId;
			this.selectedId = null;
			this.scrollToSection(section.uniqueId);
		},
		/**
		 * Scroll the preview so a section is visible.
		 * @param {string} uniqueId
		 */
		scrollToSection(uniqueId) {
			this.$nextTick(() => {
				const node = this.$el && this.$el.querySelector('.db2-section[data-section-id="' + uniqueId + '"]');
				if (node && node.scrollIntoView) node.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
			});
		},
		/**
		 * Select a section and clear the card selection.
		 * @param {string} uniqueId
		 */
		selectSection(uniqueId) {
			this.selectedSectionId = uniqueId || null;
			this.selectedId = null;
		},
		/**
		 * Select a card and its parent section.
		 * @param {string} uniqueId
		 */
		selectCard(uniqueId) {
			this.selectedId = uniqueId || null;
			const card = (this.draft.cards || []).find(c => c.uniqueId === uniqueId);
			if (card) this.selectedSectionId = card.sectionId;
		},
		/**
		 * Apply a new section order from a header drag.
		 * @param {string[]} ids
		 */
		reorderSections(ids) {
			const list = this.draft.sections || [];
			const current = list.slice().sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)).map(s => s.uniqueId).join('\0');
			const nextIds = (ids || []).filter(id => list.some(s => s.uniqueId === id));
			if (nextIds.join('\0') === current) return;
			if (!this._sectionReorderUndo) {
				this.pushUndo();
				this._sectionReorderUndo = true;
			}
			const byId = {};
			list.forEach(s => {
				byId[s.uniqueId] = s;
			});
			this.draft.sections = nextIds.map((id, i) => ({ ...byId[id], sortOrder: i }));
		},
		/**
		 * Close the section-reorder undo grouping.
		 */
		onSectionReorderEnd() {
			this._sectionReorderUndo = false;
		},
		/**
		 * Expand or collapse a section.
		 * @param {{ uniqueId: string, collapsed: boolean }} payload
		 */
		toggleSectionCollapsed(payload) {
			if (!payload || !payload.uniqueId) return;
			const sections = (this.draft.sections || []).map(s => s.uniqueId === payload.uniqueId ? { ...s, collapsed: payload.collapsed === true } : s);
			this.draft = { ...this.draft, sections };
		},
		/**
		 * Rename a section.
		 * @param {{ uniqueId: string, name: string }} payload
		 */
		renameSection(payload) {
			this.pushUndo();
			this.draft.sections = (this.draft.sections || []).map(s => s.uniqueId === payload.uniqueId ? { ...s, name: payload.name } : s);
		},
		/**
		 * Open confirm before deleting a section and its cards.
		 * @param {string} uniqueId
		 */
		askRemoveSection(uniqueId) {
			if (!uniqueId) return;
			this.sectionDeleteId = uniqueId;
			this.sectionDeleteOpen = true;
		},
		/**
		 * Close the section delete dialog.
		 */
		closeSectionDelete() {
			this.sectionDeleteOpen = false;
			this.sectionDeleteId = null;
		},
		/**
		 * Delete the section after confirm.
		 */
		confirmRemoveSection() {
			const uniqueId = this.sectionDeleteId;
			this.closeSectionDelete();
			if (uniqueId) this.removeSection(uniqueId);
		},
		/**
		 * Remove a section and its cards.
		 * @param {string} uniqueId
		 */
		removeSection(uniqueId) {
			this.pushUndo();
			this.draft.sections = (this.draft.sections || []).filter(s => s.uniqueId !== uniqueId);
			this.draft.cards = (this.draft.cards || []).filter(c => c.sectionId !== uniqueId);
			if (this.selectedSectionId === uniqueId) this.selectedSectionId = null;
		},
		/**
		 * Duplicate a card.
		 * @param {string} uniqueId
		 */
		duplicateCard(uniqueId) {
			const src = (this.draft.cards || []).find(c => c.uniqueId === uniqueId);
			if (!src) return;
			this.pushUndo();
			const copy = JSON.parse(JSON.stringify(src));
			copy.uniqueId = makeId('card');
			copy.layout = findDuplicateLayout(src, this.draft.cards);
			this.draft = { ...this.draft, cards: resolveOverlaps((this.draft.cards || []).concat([copy]), copy.uniqueId) };
			this.selectedId = copy.uniqueId;
		},
		/**
		 * Delete a card.
		 * @param {string} uniqueId
		 */
		removeCard(uniqueId) {
			this.pushUndo();
			this.draft.cards = (this.draft.cards || []).filter(c => c.uniqueId !== uniqueId);
			if (this.selectedId === uniqueId) this.selectedId = null;
		},
		/**
		 * Update card layout from drag/resize. `live` packs during drag; `settle` packs on drop.
		 * @param {{ uniqueId: string, layout: object, live?: boolean, settle?: boolean, orderIds?: string[] }} payload
		 */
		moveCard(payload) {
			let cards = (this.draft.cards || []).slice();
			cards = cards.map(c => {
				if (c.uniqueId !== payload.uniqueId) return c;
				const next = { ...c };
				if (payload.layout) next.layout = snapLayout(payload.layout, c.type);
				if (payload.sectionId) next.sectionId = payload.sectionId;
				return next;
			});
			if (payload.settle || payload.live) {
				cards = resolveOverlaps(cards, payload.uniqueId, payload.orderIds);
			}
			this.draft = { ...this.draft, cards };
		},
		/**
		 * Open settings for a card.
		 * @param {string} uniqueId
		 */
		openSettings(uniqueId) {
			this.settingsCard = (this.draft.cards || []).find(c => c.uniqueId === uniqueId) || null;
			this.settingsOpen = true;
		},
		/**
		 * Apply settings dialog result.
		 * @param {object} card
		 */
		applyCardSettings(card) {
			if (!card) return;
			const next = JSON.parse(JSON.stringify(card));
			if (!next.uniqueId && this.settingsCard) next.uniqueId = this.settingsCard.uniqueId;
			if (!next.uniqueId) return;
			this.pushUndo();
			const cards = (this.draft.cards || []).slice();
			const idx = cards.findIndex(c => String(c.uniqueId) === String(next.uniqueId));
			if (idx < 0) return;
			const merged = {
				...cards[idx],
				...next,
				uniqueId: cards[idx].uniqueId,
				config: { ...(cards[idx].config || {}), ...(next.config || {}) },
				layout: next.layout ? { ...cards[idx].layout, ...next.layout } : { ...cards[idx].layout }
			};
			this.$set(cards, idx, merged);
			this.draft = { ...this.draft, cards };
			this.settingsCard = merged;
		},
        /**
         * Create a folder.
         * @param {string} name
         */
        async createFolder(name) {
            try {
                await this.$store.dispatch('createDashboardFolder', { name, project: this.projectId });
            } catch (e) {
                console.error(e);
                window.M && window.M.toast && window.M.toast({ html: 'Failed to create folder' });
                throw e;
            }
        },
        /**
         * Rename a folder.
         * @param {{ folderId: string, name: string }} payload
         */
        async renameFolder(payload) {
            try {
                await this.$store.dispatch('updateDashboardFolder', {
                    folderId: payload.folderId,
                    updateData: { name: payload.name },
                    projectId: this.projectId
                });
            } catch (e) {
                console.error(e);
                window.M && window.M.toast && window.M.toast({ html: 'Failed to rename folder' });
                throw e;
            }
        },
        /**
         * Delete a folder.
         * @param {string} folderId
         */
        async deleteFolder(folderId) {
            try {
                await this.$store.dispatch('deleteDashboardFolder', { folderId, projectId: this.projectId });
            } catch (e) {
                console.error(e);
                window.M && window.M.toast && window.M.toast({ html: 'Failed to delete folder' });
                throw e;
            }
        },
		/**
		 * Rename another dashboard from the menu.
		 * @param {{ dashboardId: string, name: string }} payload
		 */
		async renameDashboard(payload) {
			await this.$store.dispatch('updateDashboard', {
				dashboardId: payload.dashboardId,
				updateData: { name: payload.name }
			});
			await this.$store.dispatch('getDashboards', this.projectId);
			if (payload.dashboardId === this.draft.objectId) this.draft.name = payload.name;
		},
		/**
		 * Patch a listed dashboard (icon / folder).
		 * @param {{ dashboardId: string, updateData: object }} payload
		 */
		async patchListedDashboard(payload) {
			const dashboardId = payload && payload.dashboardId;
			const updateData = (payload && payload.updateData) || {};
			const prev = (this.dashboards || []).find(row => row && row.objectId === dashboardId);
			const revertData = {};
			Object.keys(updateData).forEach(key => {
				revertData[key] = prev && prev[key] !== undefined ? prev[key] : (key === 'folderId' ? '' : null);
			});
			if (dashboardId) {
				this.$store.commit('patchDashboardInList', { dashboardId, updateData });
			}
			if (dashboardId === this.draft.objectId) {
				this.draft = { ...this.draft, ...updateData };
			}
			try {
				await this.$store.dispatch('updateDashboard', payload);
			} catch (e) {
				if (dashboardId) {
					this.$store.commit('patchDashboardInList', { dashboardId, updateData: revertData });
				}
				if (dashboardId === this.draft.objectId) {
					this.draft = { ...this.draft, ...revertData };
				}
				console.error(e);
				window.M && window.M.toast && window.M.toast({ html: 'Failed to update dashboard' });
				throw e;
			}
		},
		/**
		 * Delete from the menu.
		 * @param {string} dashboardId
		 */
		async deleteListedDashboard(dashboardId) {
			await this.$store.dispatch('deleteDashboard', dashboardId);
			await this.$store.dispatch('getDashboards', this.projectId);
			if (dashboardId === this.draft.objectId) this.$emit('back');
		},
		/**
		 * Delete the open dashboard after the confirm dialog.
		 */
		async confirmDeleteDashboard() {
			if (this.deleting) return;
			this.deleting = true;
			try {
				this.deleteOpen = false;
				await this.deleteListedDashboard(this.draft.objectId);
			} catch (e) {
				console.error(e);
				window.M && window.M.toast && window.M.toast({ html: 'Failed to delete dashboard' });
			} finally {
				this.deleting = false;
			}
		}
	}
};
</script>
